import { memo, useEffect, useMemo, useState } from 'react';
import { compareDom } from '../../diff/dom-compare';
import { findRoot, getDOMParser } from "../../shared/utils/dom.utils";
import { useBookContext } from '../../state/BookContext';
import { useFileContext } from '../../state/FileContext';
import { useCaseSensitiveSearch, useLocalSearchFilter, useTextFilter, useWholeWordSearch } from '../../state/FilterContext';
import { InternalActionName, useInternalAction } from '../../state/InternalContext';
import { useParser } from '../../state/parser-hook';
import { SourceItem } from '../../shared/models/source-item.model';
import { useCurrentSearchHitIndex, usePerformSearch, useSearchHits } from '../../state/SearchContext';
import { BookOutletScopeProvider } from '../../state/BookOutletScopeContext';
import { FileOutletScopeProvider } from '../../state/FileOutletScopeContext';
import { normalizeMatchMode } from '../../utils/local-search-match.utils';

interface FileOutletProps {
    search?: "global" | "local" | "none";
    source?: "file" | "book";
    /** Runtime HTML is a string; only `"any"` enables OR mode. */
    match?: "any" | "exact";
}

export const FileOutlet = memo(function FileOutlet({ search = "none", source = "file", match }: FileOutletProps) {
    const parse = useParser();
    const { fileState } = useFileContext();
    const { bookState } = useBookContext();
    const activeContent = source === "book" ? bookState.bookContent : fileState.content;
    const searchHits = useSearchHits();
    const performSearch = usePerformSearch();
    const textFilter = useTextFilter();
    const localSearchFilter = useLocalSearchFilter();
    const caseSensitiveSearch = useCaseSensitiveSearch();
    const wholeWordSearch = useWholeWordSearch();
    const currentSearchHitIndex = useCurrentSearchHitIndex();
    const setStoredItem = useInternalAction(InternalActionName.SET_STORED_ITEM);
    const setStoredArray = useInternalAction(InternalActionName.SET_STORED_ARRAY);
    const contentKey = activeContent || "";
    const matchMode = normalizeMatchMode(match);
    const [compareOverlay, setCompareOverlay] = useState<{ forContent: string; html: string } | null>(null);

    if (compareOverlay !== null && compareOverlay.forContent !== contentKey) {
        setCompareOverlay(null);
    }

    const html = compareOverlay !== null && compareOverlay.forContent === contentKey
        ? compareOverlay.html
        : contentKey;

    useEffect(() => {
        if (search === "none") return;
        if (search === "local") {
            performSearch(localSearchFilter, caseSensitiveSearch, wholeWordSearch, matchMode);
        } else {
            performSearch(textFilter, caseSensitiveSearch, wholeWordSearch, matchMode);
        }
    }, [textFilter, localSearchFilter, caseSensitiveSearch, wholeWordSearch, search, matchMode, performSearch]);

    useEffect(() => {
        setStoredItem({ _currentSearchHitIndex: { value: currentSearchHitIndex, position: 0, props: {} } });
    }, [currentSearchHitIndex, setStoredItem]);

    useEffect(() => {
        setStoredArray({
            _searchHits: searchHits.map((hit, index) => ({
                value: hit.id,
                position: index,
                props: {
                    info: {
                        searchword: [hit.text],
                        context: [hit.blockContent],
                        title: [hit.blockHeading],
                        startOffset: [hit.startOffset],
                        endOffset: [hit.endOffset],
                    },
                }
            }))
        } as { _searchHits: Array<SourceItem> });
        setStoredItem({ _currentSearchHitIndex: { value: 0, position: 0, props: {} } });
    }, [searchHits, setStoredItem, setStoredArray]);

    useEffect(() => {
        if (activeContent && fileState.baselineContent && source === "file") {
            const contentDom = findRoot(getDOMParser().parseFromString(activeContent, "text/html"));
            const baselineDom = findRoot(getDOMParser().parseFromString(fileState.baselineContent, "text/html"));
            if (contentDom && baselineDom) {
                compareDom(baselineDom, contentDom, 5000);
                setCompareOverlay({ forContent: activeContent, html: contentDom?.outerHTML || "" });
            }
        }
    }, [activeContent, fileState.baselineContent, source]);

    const parsedHtml = useMemo(() => {
        if (!html) return null;
        return parse(html);
    }, [html, parse]);

    return <FileOutletScopeProvider value={{ inFileOutlet: source === "file" }}>
        <BookOutletScopeProvider value={{ inBookOutlet: source === "book" }}>
            <div className="__outlet" data-search={search} data-source={source} data-match={matchMode}>{parsedHtml}</div>
        </BookOutletScopeProvider>
    </FileOutletScopeProvider>;
});
